/**
 * Phase 5 — Executive Alert Settings
 * Konfigurasi threshold alert eksekutif. Permission: EXEC_ALERT_CONFIG_UBAH.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Save } from "lucide-react";

const UNIT_LABEL = { percent: "%", idr: "Rp", count: "unit" };

export default function ExecutiveAlertSettings() {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(null);
  const [err, setErr] = useState(null);
  const user = JSON.parse(localStorage.getItem("abs_user") || "null") || {};
  const canEdit = ["super_admin", "direktur_utama"].includes(user.role);

  const load = () => {
    setErr(null);
    api.get("/executive/alert-config")
      .then(({ data }) => setRows(data.items.map((r) => ({ ...r, _dirty: false }))))
      .catch((e) => {
        if (e?.response?.status === 403) setErr("Tidak berwenang mengakses konfigurasi alert.");
        else setErr(formatApiError(e));
      });
  };
  useEffect(() => { load(); }, []);

  const update = (code, field, value) => {
    setRows((prev) => prev.map((r) => r.code === code ? { ...r, [field]: value, _dirty: true } : r));
  };

  const save = async (row) => {
    setSaving(row.code);
    try {
      const payload = {
        enabled: row.enabled,
        operator: row.operator,
        warning_threshold: Number(row.warning_threshold),
        critical_threshold: Number(row.critical_threshold),
        updated_at_prev: row.updated_at,
      };
      const { data } = await api.put(`/executive/alert-config/${row.code}`, payload);
      setRows((prev) => prev.map((r) => r.code === row.code ? { ...data, _dirty: false } : r));
      toast.success(`Konfigurasi ${row.label} disimpan.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(null); }
  };

  if (err) {
    return (
      <div className="p-6 space-y-4" data-testid="alert-settings-page">
        <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
          <ArrowLeft size={12} className="mr-1" />Kembali
        </Link>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="alert-settings-no-access">
          {err}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="alert-settings-page">
      <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
        <ArrowLeft size={12} className="mr-1" />Kembali ke Dashboard Eksekutif
      </Link>
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Konfigurasi Peringatan Eksekutif</h1>
        <p className="text-xs md:text-sm text-slate-500">
          Threshold rule untuk alert di Dashboard Eksekutif. {canEdit ? "" : "Mode read-only."} Setiap perubahan tercatat di audit trail.
        </p>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Daftar Rule</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Kode</TableHead>
              <TableHead>Label</TableHead>
              <TableHead>Kategori</TableHead>
              <TableHead>Aktif</TableHead>
              <TableHead>Operator</TableHead>
              <TableHead className="text-right">Warning</TableHead>
              <TableHead className="text-right">Critical</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Diubah</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.code} data-testid={`alert-row-${r.code}`}>
                  <TableCell className="text-xs font-mono">{r.code}</TableCell>
                  <TableCell>{r.label}</TableCell>
                  <TableCell><Badge variant="outline">{r.category}</Badge></TableCell>
                  <TableCell>
                    <Switch checked={!!r.enabled} disabled={!canEdit}
                              onCheckedChange={(v) => update(r.code, "enabled", v)}
                              data-testid={`alert-enabled-${r.code}`} />
                  </TableCell>
                  <TableCell className="text-xs">{r.operator}</TableCell>
                  <TableCell className="text-right">
                    <Input type="number" step="any" value={r.warning_threshold}
                             disabled={!canEdit}
                             onChange={(e) => update(r.code, "warning_threshold", e.target.value)}
                             className="w-24 ml-auto text-right"
                             data-testid={`alert-warning-${r.code}`} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Input type="number" step="any" value={r.critical_threshold}
                             disabled={!canEdit}
                             onChange={(e) => update(r.code, "critical_threshold", e.target.value)}
                             className="w-24 ml-auto text-right"
                             data-testid={`alert-critical-${r.code}`} />
                  </TableCell>
                  <TableCell className="text-xs">{UNIT_LABEL[r.unit] || r.unit}</TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {r.updated_at ? new Date(r.updated_at).toLocaleDateString("id-ID") : "—"}
                    <div className="text-[10px]">oleh {r.updated_by || "—"}</div>
                  </TableCell>
                  <TableCell>
                    {canEdit && (
                      <Button variant="outline" size="sm"
                                disabled={!r._dirty || saving === r.code}
                                onClick={() => save(r)}
                                data-testid={`alert-save-${r.code}`}>
                        <Save size={12} className="mr-1" />Simpan
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
